/**
 * Promotional Sale & Product Spotlight Modal
 * Simplified, clean, and easy-to-maintain JavaScript.
 */

// Safely get item from browser localStorage
function getStorage(key) {
    try {
        return localStorage.getItem(key);
    } catch (error) {
        return null;
    }
}

// Safely save item to browser localStorage
function setStorage(key, value) {
    try {
        localStorage.setItem(key, value);
    } catch (error) {
        // Ignored if private browsing blocks storage
    }
}

// Format single digits with a leading zero (e.g. 5 -> "05")
function formatNumber(num) {
    if (num < 10) {
        return '0' + num;
    }
    return '' + num;
}

class PromotionalPopup extends HTMLElement {
    connectedCallback() {
        this.dialog = this.querySelector('.promo-popup__dialog');
        if (!this.dialog) return;

        // Settings from HTML data attributes
        this.triggerType = this.getAttribute('data-trigger') || 'delay';
        const delaySeconds = parseInt(this.getAttribute('data-delay') || '3', 10);
        this.delayMs = delaySeconds * 1000;
        const scrollPct = parseInt(this.getAttribute('data-scroll-percentage') || '20', 10);
        this.scrollPercentage = isNaN(scrollPct) ? 20 : Math.min(Math.max(scrollPct, 10), 50);
        this.frequency = this.getAttribute('data-frequency') || 'once';

        // Read customizable hours to wait before showing again (default: 24)
        const customHours = parseFloat(this.getAttribute('data-reappear-hours') || '24');
        this.reappearHours = isNaN(customHours) || customHours <= 0 ? 24 : customHours;

        // LocalStorage keys (scoped to popup ID so multiple sections don't clash)
        const id = this.id || 'default';
        this.storageKeyOnce = 'promo_popup_seen_' + id;
        this.storageKeyClosed = 'promo_popup_closed_' + id;

        // Timers & previous focus state
        this.popupTimer = null;
        this.countdownTimer = null;
        this.previousFocusElement = null;

        // Initialize features
        this.initPopup();
        this.initCountdown();
        this.attachEvents();
    }

    disconnectedCallback() {
        clearTimeout(this.popupTimer);
        clearInterval(this.countdownTimer);

        if (this.handleScrollTrigger) {
            window.removeEventListener('scroll', this.handleScrollTrigger);
        }

        if (this.handleKeyDown) {
            document.removeEventListener('keydown', this.handleKeyDown);
        }
        if (this.handleTouchMove) {
            this.removeEventListener('touchmove', this.handleTouchMove);
        }
        if (this.handleWheel) {
            this.removeEventListener('wheel', this.handleWheel);
        }

        const otherOpenPopup = document.querySelector('promotional-popup[open]');
        if (!otherOpenPopup) {
            document.documentElement.classList.remove('overflow-hidden');
            document.body.classList.remove('overflow-hidden');
        }
    }

    /* ==========================================================================
       POPUP LOGIC (SHOW / HIDE)
       ========================================================================== */

    initPopup() {
        if (this.hasAttribute('open')) return;

        if (this.shouldShowPopup()) {
            if (this.triggerType === 'scroll') {
                this.initScrollTrigger();
            } else {
                this.popupTimer = setTimeout(() => {
                    this.openPopup();
                }, this.delayMs);
            }
        }
    }

    initScrollTrigger() {
        let ticking = false;

        this.handleScrollTrigger = () => {
            if (this.hasAttribute('open')) {
                window.removeEventListener('scroll', this.handleScrollTrigger);
                return;
            }

            if (!ticking) {
                window.requestAnimationFrame(() => {
                    const scrollTop = window.scrollY || document.documentElement.scrollTop || 0;
                    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
                    const scrollPercent = docHeight > 0 ? (scrollTop / docHeight) * 100 : 100;

                    if (scrollPercent >= this.scrollPercentage) {
                        window.removeEventListener('scroll', this.handleScrollTrigger);
                        this.openPopup();
                    }
                    ticking = false;
                });
                ticking = true;
            }
        };

        window.addEventListener('scroll', this.handleScrollTrigger, { passive: true });
        // Check immediately in case the page is already scrolled past the threshold
        this.handleScrollTrigger();
    }

    shouldShowPopup() {
        if (this.hasAttribute('open')) return false;

        // Option 1: Show once to visitor
        if (this.frequency === 'once') {
            return getStorage(this.storageKeyOnce) !== 'true';
        }

        // Option 2: Show after custom hours after closing it
        if (this.frequency === 'hours' || this.frequency === '24_hours') {
            const lastClosed = getStorage(this.storageKeyClosed);
            if (!lastClosed) {
                return true; // First visit: show popup
            }

            // Calculate hours passed since they closed it
            const hoursPassed = (Date.now() - Number(lastClosed)) / (1000 * 60 * 60);
            return hoursPassed >= this.reappearHours;
        }

        return true;
    }

    openPopup() {
        if (this.handleScrollTrigger) {
            window.removeEventListener('scroll', this.handleScrollTrigger);
        }
        clearTimeout(this.popupTimer);

        this.previousFocusElement = document.activeElement;
        this.removeAttribute('inert');
        this.setAttribute('open', '');
        this.setAttribute('aria-hidden', 'false');
        document.documentElement.classList.add('overflow-hidden');
        document.body.classList.add('overflow-hidden');

        // If setting is 'once', mark as seen right away
        if (this.frequency === 'once') {
            setStorage(this.storageKeyOnce, 'true');
        }

        // Focus CTA button or close button for accessibility
        const button = this.querySelector('.promo-popup__cta-button') || this.querySelector('.promo-popup__close');
        if (button) {
            button.focus();
        }
    }

    closePopup() {
        if (!this.hasAttribute('open')) return;

        // 1. Move focus OUT of the popup BEFORE setting aria-hidden
        // This prevents the Chrome "Blocked aria-hidden on focused element" warning
        if (this.contains(document.activeElement)) {
            document.activeElement.blur();
        }

        if (this.previousFocusElement && typeof this.previousFocusElement.focus === 'function') {
            this.previousFocusElement.focus();
        }

        // 2. Now safe to mark as hidden
        this.removeAttribute('open');
        this.setAttribute('aria-hidden', 'true');
        this.setAttribute('inert', '');

        const otherOpenPopup = document.querySelector('promotional-popup[open]');
        if (!otherOpenPopup) {
            document.documentElement.classList.remove('overflow-hidden');
            document.body.classList.remove('overflow-hidden');
        }

        if (this.handleScrollTrigger) {
            window.removeEventListener('scroll', this.handleScrollTrigger);
        }
        clearTimeout(this.popupTimer);

        // Save close state to localStorage
        if (this.frequency === 'once') {
            setStorage(this.storageKeyOnce, 'true');
        } else {
            // Save the exact timestamp to start the customizable snooze countdown
            setStorage(this.storageKeyClosed, Date.now().toString());
        }
    }

    /* ==========================================================================
       KEYBOARD FOCUS TRAP (ACCESSIBILITY)
       ========================================================================== */

    handleFocusTrap(event) {
        const focusable = this.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    }

    /* ==========================================================================
       COUNTDOWN TIMER
       ========================================================================== */

    initCountdown() {
        const dateStr = this.getAttribute('data-timer-end');
        if (!dateStr) return;

        // Parse date for all browsers (replaces space with 'T')
        const formattedDate = dateStr.trim().replace(' ', 'T');
        const targetTime = Date.parse(formattedDate);
        if (isNaN(targetTime)) return;

        const daysEl = this.querySelector('[data-unit="days"]');
        const hoursEl = this.querySelector('[data-unit="hours"]');
        const minutesEl = this.querySelector('[data-unit="minutes"]');
        const secondsEl = this.querySelector('[data-unit="seconds"]');

        if (!secondsEl) return;

        const updateTimer = () => {
            const remaining = targetTime - Date.now();

            if (remaining <= 0) {
                daysEl.textContent = '00';
                hoursEl.textContent = '00';
                minutesEl.textContent = '00';
                secondsEl.textContent = '00';
                clearInterval(this.countdownTimer);
                return;
            }

            const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
            const hours = Math.floor((remaining / (1000 * 60 * 60)) % 24);
            const minutes = Math.floor((remaining / (1000 * 60)) % 60);
            const seconds = Math.floor((remaining / 1000) % 60);

            this.setDigit(daysEl, formatNumber(days));
            this.setDigit(hoursEl, formatNumber(hours));
            this.setDigit(minutesEl, formatNumber(minutes));
            this.setDigit(secondsEl, formatNumber(seconds));
        };

        // Run once immediately, then tick every second
        updateTimer();
        this.countdownTimer = setInterval(updateTimer, 1000);
    }

    setDigit(element, value) {
        if (!element || element.textContent === value) return;

        element.textContent = value;
        element.classList.remove('is-changing');
        requestAnimationFrame(() => {
            element.classList.add('is-changing');
        });
    }

    /* ==========================================================================
       EVENT LISTENERS
       ========================================================================== */

    attachEvents() {
        // Close on clicking 'X' button or backdrop
        this.addEventListener('click', (event) => {
            if (event.target.closest('[data-action="close"]')) {
                event.preventDefault();
                this.closePopup();
            }
        });

        // Close on Escape, keep focus trapped on Tab
        this.handleKeyDown = (event) => {
            if (!this.hasAttribute('open')) return;

            if (event.key === 'Escape') {
                this.closePopup();
            } else if (event.key === 'Tab') {
                this.handleFocusTrap(event);
            }
        };
        document.addEventListener('keydown', this.handleKeyDown);

        // Prevent background scrolling via wheel or touch gestures outside dialog
        this.handleTouchMove = (event) => {
            if (!this.hasAttribute('open')) return;
            if (!event.target.closest('.promo-popup__dialog')) {
                event.preventDefault();
            }
        };
        this.addEventListener('touchmove', this.handleTouchMove, { passive: false });

        this.handleWheel = (event) => {
            if (!this.hasAttribute('open')) return;
            if (!event.target.closest('.promo-popup__dialog')) {
                event.preventDefault();
            }
        };
        this.addEventListener('wheel', this.handleWheel, { passive: false });

        // Shopify Theme Editor live preview support
        if (window.Shopify && window.Shopify.designMode) {
            const section = this.closest('.shopify-section');
            if (section) {
                section.addEventListener('shopify:section:select', () => this.openPopup());
                section.addEventListener('shopify:section:deselect', () => this.closePopup());
            }
        }
    }
}

// Register custom element
if (!customElements.get('promotional-popup')) {
    customElements.define('promotional-popup', PromotionalPopup);
}